type ChangeFrequency = 'daily' | 'weekly' | 'monthly';

export type PublicPageTargetId = 'about' | 'contact' | 'create' | 'explore' | 'signIn' | 'tutorials';

export interface PublicPageTarget {
  id: PublicPageTargetId;
  href: string;
  navMessageId?: string;
  footerMessageId?: string;
  includeInFooterHighlight: boolean;
  includeInNav: boolean;
  includeInSitemap: boolean;
  changefreq: ChangeFrequency;
  priority: number;
}

export interface PublicPageTargetContext {
  playgroundUrl: string;
}

export interface LandingSitemapContext extends PublicPageTargetContext {
  lastmod: string;
  siteUrl: string;
}

function joinUrl(baseUrl: string, path: string) {
  return `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

function normalizeSiteUrl(siteUrl: string) {
  return siteUrl.replace(/\/+$/, '');
}

function formatSitemapUrlEntry({
  loc,
  lastmod,
  changefreq,
  priority,
}: {
  loc: string;
  lastmod: string;
  changefreq: ChangeFrequency;
  priority: number;
}) {
  return [
    '  <url>',
    `    <loc>${loc}</loc>`,
    `    <lastmod>${lastmod}</lastmod>`,
    `    <changefreq>${changefreq}</changefreq>`,
    `    <priority>${priority}</priority>`,
    '  </url>',
  ].join('\n');
}

export function buildPublicPageTargets({ playgroundUrl }: PublicPageTargetContext): PublicPageTarget[] {
  return [
    {
      id: 'create',
      href: joinUrl(playgroundUrl, '/projects/editor'),
      navMessageId: 'TopBanner.NavBarButtons.create',
      footerMessageId: 'Footer.Highlight.create',
      includeInFooterHighlight: true,
      includeInNav: true,
      includeInSitemap: true,
      changefreq: 'weekly',
      priority: 0.9,
    },
    {
      id: 'tutorials',
      href: joinUrl(playgroundUrl, '/tutorials'),
      navMessageId: 'TopBanner.NavBarButtons.hourOfCode',
      footerMessageId: 'Footer.Highlight.tutorials',
      includeInFooterHighlight: true,
      includeInNav: true,
      includeInSitemap: true,
      changefreq: 'weekly',
      priority: 0.85,
    },
    {
      id: 'explore',
      href: joinUrl(playgroundUrl, '/explore'),
      navMessageId: 'TopBanner.NavBarButtons.explore',
      footerMessageId: 'Footer.Highlight.explore',
      includeInFooterHighlight: true,
      includeInNav: true,
      includeInSitemap: true,
      changefreq: 'daily',
      priority: 0.8,
    },
    {
      id: 'about',
      href: joinUrl(playgroundUrl, '/about'),
      navMessageId: 'TopBanner.NavBarButtons.about',
      footerMessageId: 'Footer.Highlight.about-us',
      includeInFooterHighlight: true,
      includeInNav: true,
      includeInSitemap: true,
      changefreq: 'monthly',
      priority: 0.7,
    },
    {
      id: 'contact',
      href: joinUrl(playgroundUrl, '/contact'),
      navMessageId: 'TopBanner.NavBarButtons.contact',
      footerMessageId: 'Footer.Highlight.contact',
      includeInFooterHighlight: true,
      includeInNav: true,
      includeInSitemap: true,
      changefreq: 'monthly',
      priority: 0.65,
    },
    {
      id: 'signIn',
      href: joinUrl(playgroundUrl, '/sign-in'),
      includeInFooterHighlight: false,
      includeInNav: false,
      includeInSitemap: true,
      changefreq: 'monthly',
      priority: 0.6,
    },
  ];
}

export function buildLandingSitemapXml({ lastmod, playgroundUrl, siteUrl }: LandingSitemapContext) {
  const normalizedSiteUrl = normalizeSiteUrl(siteUrl);
  const entries = [
    formatSitemapUrlEntry({
      loc: `${normalizedSiteUrl}/`,
      lastmod,
      changefreq: 'monthly',
      priority: 1,
    }),
    ...buildPublicPageTargets({ playgroundUrl })
      .filter((target) => target.includeInSitemap)
      .map((target) =>
        formatSitemapUrlEntry({
          loc: target.href,
          lastmod,
          changefreq: target.changefreq,
          priority: target.priority,
        }),
      ),
  ];

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    '',
    ...entries,
    '',
    '</urlset>',
  ].join('\n');
}
